/**
 * SnapTrade brokerage-connection, sync and admin endpoints.
 */

import type { Transaction, TransactionType } from '@luminaria/shared';
import type {
  Brokerage,
  DailySyncResponse,
  LinkAccountsRequest,
  PendingAccountsResponse,
  SnaptradeAdminUsersData,
  SnaptradeConnection,
  SnaptradeDebugData,
  SnaptradeStatus,
  SyncResult,
  TransactionSyncResult,
} from '../hooks/snaptrade/types';
import { apiClient } from './client';
import { API_ENDPOINTS } from './endpoints';

interface LinkAccountsResult {
  connectionId: string | null;
  connectionIds: string[];
  linkedAccounts: string[];
  syncResults: SyncResult[];
}

export interface TransactionConflictResolution {
  accountId: string;
  externalId: string;
  newData: {
    action?: string;
    symbol?: string;
    quantity?: number;
    price?: number;
    amount?: number;
    description?: string;
    securityType?: string;
  };
}

export const snaptradeApi = {
  async status(): Promise<SnaptradeStatus> {
    const { data } = await apiClient.get<SnaptradeStatus>(API_ENDPOINTS.SNAPTRADE.STATUS);
    return data;
  },

  async connections(): Promise<SnaptradeConnection[]> {
    const { data } = await apiClient.get<{ connections: SnaptradeConnection[] }>(API_ENDPOINTS.SNAPTRADE.CONNECTIONS);
    return data.connections;
  },

  async pendingAccounts(authorizationId: string | undefined): Promise<PendingAccountsResponse> {
    const { data } = await apiClient.get<PendingAccountsResponse>(API_ENDPOINTS.SNAPTRADE.PENDING_ACCOUNTS, {
      params: authorizationId ? { authorizationId } : {},
    });
    return data;
  },

  async brokerages(): Promise<Brokerage[]> {
    const { data } = await apiClient.get<{ brokerages: Brokerage[] }>(API_ENDPOINTS.SNAPTRADE.BROKERAGES);
    return data.brokerages;
  },

  /** OAuth portal URL for connecting (or re-authorizing) a brokerage. */
  /**
   * A connection-portal URL. Pass `reconnect` (the disabled connection's
   * authorization id) to repair that connection; without it SnapTrade creates
   * a new connection with new account ids.
   */
  async connect(request: { broker?: string; reconnect?: string } = {}): Promise<string> {
    const { data } = await apiClient.post<{ url: string }>(API_ENDPOINTS.SNAPTRADE.CONNECT, request);
    return data.url;
  },

  /** The user returned from reconnecting this connection: the server refreshes and syncs it. */
  async reconnected(connectionId: string): Promise<{ accountIds: string[] }> {
    const { data } = await apiClient.post<{ accountIds: string[] }>(API_ENDPOINTS.SNAPTRADE.RECONNECTED(connectionId));
    return data;
  },

  async linkAccounts(request: LinkAccountsRequest): Promise<LinkAccountsResult> {
    const { data } = await apiClient.post<LinkAccountsResult>(API_ENDPOINTS.SNAPTRADE.LINK_ACCOUNTS, request);
    return data;
  },

  async disconnect(connectionId: string): Promise<void> {
    await apiClient.delete(API_ENDPOINTS.SNAPTRADE.CONNECTION(connectionId));
  },

  async syncAccount(accountId: string): Promise<SyncResult> {
    const { data } = await apiClient.post<{ result: SyncResult }>(API_ENDPOINTS.SNAPTRADE.SYNC(accountId));
    return data.result;
  },

  async dailySync(): Promise<DailySyncResponse> {
    const { data } = await apiClient.post<DailySyncResponse>(API_ENDPOINTS.SNAPTRADE.DAILY_SYNC);
    return data;
  },

  /** Tell SnapTrade to pull fresh (uncached) brokerage data, then re-sync the connection's accounts. */
  async refreshConnection(connectionId: string): Promise<{ refreshResult: unknown; syncResults: SyncResult[] }> {
    const { data } = await apiClient.post<{ refreshResult: unknown; syncResults: SyncResult[] }>(
      API_ENDPOINTS.SNAPTRADE.REFRESH(connectionId)
    );
    return data;
  },

  /** Re-fetch a year of transactions for a connection, optionally one account only. */
  async refreshTransactions(input: { connectionId: string; accountId?: string }): Promise<{ syncResults: TransactionSyncResult[] }> {
    const { data } = await apiClient.post<{ syncResults: TransactionSyncResult[] }>(
      API_ENDPOINTS.SNAPTRADE.REFRESH_TRANSACTIONS(input.connectionId),
      undefined,
      { params: input.accountId ? { accountId: input.accountId } : undefined }
    );
    return data;
  },

  async resolveTransactionConflictsBatch(conflicts: TransactionConflictResolution[]): Promise<{ updated: number; notFound: string[] }> {
    const { data } = await apiClient.post<{ updated: number; notFound: string[] }>(API_ENDPOINTS.SNAPTRADE.RESOLVE_CONFLICTS_BATCH, {
      conflicts,
    });
    return data;
  },

  async updateOptionClassification(input: { accountId: string; externalId: string; action: TransactionType }): Promise<Transaction> {
    const { data } = await apiClient.post<{ transaction: Transaction }>(API_ENDPOINTS.SNAPTRADE.UPDATE_OPTION_CLASSIFICATION, input);
    return data.transaction;
  },

  async debug(): Promise<SnaptradeDebugData> {
    const { data } = await apiClient.get<SnaptradeDebugData>(API_ENDPOINTS.SNAPTRADE.DEBUG);
    return data;
  },

  async deleteAuthorization(authorizationId: string): Promise<void> {
    await apiClient.delete(API_ENDPOINTS.SNAPTRADE.AUTHORIZATION(authorizationId));
  },

  async adminUsers(): Promise<SnaptradeAdminUsersData> {
    const { data } = await apiClient.get<SnaptradeAdminUsersData>(API_ENDPOINTS.SNAPTRADE.ADMIN_USERS);
    return data;
  },

  async deleteAdminUser(snaptradeUserId: string): Promise<void> {
    await apiClient.delete(API_ENDPOINTS.SNAPTRADE.ADMIN_USER(snaptradeUserId));
  },

  async resetCredentials(): Promise<void> {
    await apiClient.post(API_ENDPOINTS.SNAPTRADE.RESET_CREDENTIALS);
  },

  async deleteLocalConnection(connectionId: string): Promise<void> {
    await apiClient.delete(API_ENDPOINTS.SNAPTRADE.LOCAL_CONNECTION(connectionId));
  },
};
